import { useFormik } from 'formik';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { routeFor } from 'src/core/constants/routes';

import { CustomFieldIsRequiredFormField } from './components/CustomFieldIsRequiredFormField';
import { CustomFieldNameFormField } from './components/CustomFieldNameFormField';
import { validate } from './form';
import { type FormValues } from './types';
import { routes } from '../../routes';
import { getHelpVideoUrl } from '../utils';

export const ControlSettingsCustomFieldName = () => {
  const { t, activeLanguage } = useTranslation('global');
  const history = useHistory();

  const companyId = useCompanyId();

  const [hasSubmittedOnce, setHasSubmittedOnce] = useState<boolean>(false);

  const { errors, values, handleChange, handleSubmit, setFieldValue } =
    useFormik<FormValues>({
      initialValues: {
        name: history.location.state?.name ?? '',
        isRequired: history.location.state?.isRequired ?? true,
      },
      validate: (valuesToValidate) =>
        validate({
          values: valuesToValidate,
          t,
        }),
      validateOnChange: hasSubmittedOnce,
      onSubmit: (customFieldValues) => {
        const nextPagePath = routeFor(
          routes.CONTROL_SETTINGS_CUSTOM_FIELD_VALUES.path,
          {
            company: companyId,
          },
        );
        history.push(nextPagePath, { ...customFieldValues });
      },
    });

  const onContinue = async () => {
    if (!hasSubmittedOnce) {
      setHasSubmittedOnce(true);
    }
    handleSubmit();
  };

  const goBackToHub = () => {
    const hubPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(hubPath);
  };

  return (
    <TaskStepLayout
      onBack={goBackToHub}
      onNext={onContinue}
      nextButtonText={t('misc.continue')}
      title={t('setupHub.customField.titleName')}
      description={t('setupHub.customField.callout.description')}
      video={{
        url: getHelpVideoUrl('custom_fields', activeLanguage),
        title: t('misc.needHelp'),
      }}
    >
      <form className="flex flex-col gap-24" onSubmit={handleSubmit}>
        <CustomFieldNameFormField
          error={errors.name}
          name={values.name}
          onChange={handleChange}
        />
        <CustomFieldIsRequiredFormField
          isRequired={values.isRequired}
          onChange={(isRequired) => {
            setFieldValue('isRequired', isRequired);
          }}
        />
      </form>
    </TaskStepLayout>
  );
};
